import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, sidebarNavTestId } from '../test-ids';

export default defineFeature({
  id: 'customers.customer.update',
  category: 'customers',
  title: 'Modifier un client et faire des actions groupées',
  intent:
    "Mettre à jour la fiche d'un client (coordonnées, commentaire, tags, préférences marketing), agir sur plusieurs clients d'un coup, et si besoin le bloquer de la réservation en ligne ou exporter ses données.",
  context: {
    why: "Une base clients à jour est le socle d'une relation fiable : c'est elle qui garantit que vos confirmations et vos rappels arrivent à la bonne adresse, que vos campagnes touchent les bons profils, et que l'historique de chaque client reste exploitable. Sur le terrain, une coordonnée fausse, c'est un rappel qui n'arrive pas et un rendez-vous manqué ; un consentement mal renseigné, c'est un client contacté à tort ou, à l'inverse, oublié d'une campagne à laquelle il aurait été réceptif. La modification individuelle sert à corriger et enrichir une fiche au fil de l'eau ; les actions groupées font gagner un temps considérable quand la même opération doit s'appliquer à des dizaines de clients d'un coup — ajouter un tag à tout un segment, ajuster les préférences marketing, ou exporter une sélection. Le blocage, lui, protège votre agenda : un client qui pose des lapins à répétition peut être empêché de réserver en ligne, sans que vous ayez à surveiller manuellement chaque prise de rendez-vous.",
    use_cases: [
      'Corriger un e-mail ou un numéro erroné pour que les notifications repartent au bon endroit.',
      'Ajouter un tag « VIP » ou « Pro » à toute une sélection de clients en une seule action groupée.',
      "Ajuster les préférences marketing (e-mail et SMS) d'un ensemble de clients d'un coup.",
      "Bloquer un client qui annule systématiquement à la dernière minute pour l'empêcher de réserver en ligne.",
      "Exporter les données d'un client à sa demande, pour répondre à une obligation RGPD.",
    ],
  },
  required_scopes: ['customer.update', 'customer.delete'],
  ui_entry_points: [
    {
      route: '/customers/custom-fields',
      element: 'Configuration, aperçu, archivage et restauration des champs personnalisés',
    },
    { route: '/customers', element: 'Crayon « Modifier » sur une ligne de la liste des clients' },
    { route: '/customers', element: "Bouton « Sélectionner » puis barre d'actions groupées en bas de la liste" },
  ],
  api: {
    primary_operation_id: 'ProCustomerAppController_putProCustomer',
    related_operation_ids: [
      'ProCustomerAppController_bulkUpdateProCustomer',
      'ProCustomerAppController_bulkDeleteProCustomer',
      'ProCustomerAppController_exportProCustomer',
      'ProCustomerAppController_banProCustomer',
      'ProCustomerAppController_unbanProCustomer',
      'ProCustomerAppController_getProCustomer',
    ],
  },
  steps: [
    'Dans « Champs personnalisés », ajoutez un champ, choisissez son type et ses choix éventuels, testez son aperçu puis enregistrez. Les champs archivés peuvent être restaurés sur le même écran.',
    'Dans la fiche client, renseignez ou videz les informations personnalisées puis enregistrez.',
    '**Ouvrez la fiche** du client à modifier depuis la liste des clients, puis cliquez sur le crayon « Modifier ». Vous pouvez aussi ouvrir sa fiche détaillée et cliquer sur « Modifier ».',
    "**Mettez à jour ses informations** : coordonnées (e-mail, téléphone, adresse), commentaire interne, tags, et préférences marketing (accepte-t-il les e-mails ? les SMS ?). Seul l'e-mail reste obligatoire.",
    "**Enregistrez** : la fiche est mise à jour immédiatement et les changements s'appliquent aux prochaines communications.",
    "**Pour agir sur plusieurs clients**, cliquez sur « Sélectionner », puis cochez les clients concernés — ou « tout sélectionner » pour viser l'ensemble de la liste filtrée.",
    "**Choisissez l'action groupée** dans la barre qui apparaît en bas : « Modifier » (préférences marketing e-mail et SMS, commentaire commun, ajout ou retrait de tags sur toute la sélection), « Supprimer » ou « Exporter ».",
    '**Pour bloquer un client**, appliquez-lui le tag Propelsia « bloqué » : il ne pourra plus réserver en ligne. Retirez ce tag pour le débloquer.',
  ],
  gotchas: [
    'Un brouillon de configuration est conservé pendant les changements de page de la session. Le menu Clients et le bouton Retour aux clients conservent tous deux le brouillon. Seule l’action Abandonner le brouillon le supprime après confirmation ; recharger ou fermer le navigateur demande confirmation.',
    'Renommer un choix conserve les réponses déjà saisies. Un choix retiré reste affiché comme ancien choix en modification de fiche.',
    'L’archivage conserve les valeurs en base et les masque dans les fiches ; la restauration les rend à nouveau visibles. Les autres modifications en cours restent dans le formulaire.',
    'Si un enregistrement échoue après plusieurs lignes, les lignes déjà enregistrées restent acquises ; les autres restent à corriger et à enregistrer.',
    "**Les actions groupées s'appliquent à TOUTE la sélection.** Taguer ou détaguer, ajouter un commentaire commun, changer les préférences marketing (e-mail et SMS), supprimer ou exporter : l'opération vaut pour tous les clients cochés à la fois. C'est le principal gain de temps de la rubrique — pensez-y dès que la même modification concerne plus de deux ou trois fiches.",
    "**Bloquer un client passe par un tag Propelsia « bloqué ».** Il n'y a pas de bouton « bannir » distinct : le blocage est un tag spécial géré par Propelsia. Une fois posé, le client ne peut plus réserver en ligne ; retirer le tag le débloque. Ce même tag se pose aussi en action groupée.",
    "**L'export répond aux demandes RGPD.** Un client peut demander une copie de ses données : l'action « Exporter » produit ce fichier. Utile aussi pour reprendre une partie de votre base dans un autre outil.",
    "**Modifier les préférences marketing change l'éligibilité aux campagnes.** Si vous décochez le consentement e-mail ou SMS d'un client, il sort automatiquement des ciblages de campagne correspondants. À l'inverse, ne cochez jamais un consentement à la place du client : le consentement doit venir de lui.",
    "**Seul l'e-mail est obligatoire.** Comme à la création, vous pouvez laisser les autres champs vides et compléter plus tard — une fiche incomplète vaut mieux qu'une prise de rendez-vous bloquée.",
    "**La suppression est définitive.** Supprimer un client (à l'unité ou en groupe) efface sa fiche ; son historique de rendez-vous n'est plus rattaché à un client identifié. En cas de doute, préférez le blocage à la suppression.",
  ],
  related_features: ['customers.customer.read', 'customers.tag.manage', 'agenda.appointment.create'],
  demo_script: {
    duration_target_s: 45,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption:
          'Une base à jour, c’est la garantie que vos rappels et campagnes touchent les bons clients : mettons une fiche à jour.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/customers'),
        caption: 'Menu latéral, Clients : on retrouve la fiche à corriger.',
      },
      { action: 'wait_for', target: TEST_IDS.customers_add, caption: 'La liste s’affiche.' },
      {
        action: 'click',
        target: TEST_IDS.list_row_edit,
        caption: 'On ouvre l’édition du client d’un clic sur le crayon.',
        capture: true,
      },
      { action: 'wait_for', target: fieldTestId('email'), caption: 'Le formulaire d’édition s’ouvre.' },
      {
        action: 'fill',
        target: fieldTestId('email'),
        value: 'camille.durand@exemple.fr',
        caption: 'On corrige l’e-mail : une coordonnée juste, c’est un rappel qui arrive vraiment.',
        capture: true,
      },
      { action: 'click', target: TEST_IDS.modal_submit, caption: 'On enregistre.' },
      {
        action: 'expect_visible',
        target: TEST_IDS.customers_add,
        caption: 'La fiche est à jour et s’appliquera dès vos prochaines communications.',
        capture: true,
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Sélectionner un/tous les clients → barre en bas : modifier, supprimer,
// > exporter. « Modifier » = action groupée : préférences marketing (mailing ET SMS),
// > commentaire groupé, ajouter des tags, retirer des tags sur toute la sélection.
